import {
    registerInpInteractionListener,
    startTrackingINP,
    startTrackingLongTasks,
    startTrackingWebVitals,
    addPerformanceEntries,
} from '@sentry-internal/browser-utils';
import * as Sentry from '@sentry/browser';
import {
    addNonEnumerableProperty,
    browserPerformanceTimeOrigin,
    Client,
    generateTraceId,
    getCurrentScope,
    getDynamicSamplingContextFromSpan,
    getIsolationScope,
    Integration,
    parseUrl,
    registerSpanErrorInstrumentation,
    SPAN_STATUS_ERROR,
    SpanAttributes,
    spanIsSampled,
    StartSpanOptions,
} from '@sentry/core';
import { get } from 'lodash';

const SCOPE_SPAN_FIELD = '_sentrySpan';
const ACTIVE_IDLE_SPAN_PROPERTY = '_sentry_idleSpan';

function setActiveIdleSpan(client: Client, span: Sentry.Span | undefined) {
    addNonEnumerableProperty(client, ACTIVE_IDLE_SPAN_PROPERTY, span);
}

function _setSpanForScope(scope: Sentry.Scope, span: Sentry.Span | undefined) {
    if (span) {
        addNonEnumerableProperty(scope, SCOPE_SPAN_FIELD, span);
    } else {
        delete (scope as unknown as Record<typeof SCOPE_SPAN_FIELD, Sentry.Span | undefined>)[
            SCOPE_SPAN_FIELD
        ];
    }
}

function _addPerformanceEntries(span: Sentry.Span) {
    const origin = browserPerformanceTimeOrigin;
    if (!performance?.getEntries || !origin) {
        // Gatekeeper if performance API not available
        return;
    }
    const performanceEntries = performance.getEntries();
    const timeOrigin = origin / 1000;

    const navigateEntry = performanceEntries.find((entry) => entry.entryType === 'navigation');
    performanceEntries.forEach((entry) => {
        switch (entry.entryType) {
            case 'resource':
                _addResourceSpan(span, entry as PerformanceResourceTiming, timeOrigin);
                break;
            case 'mark':
            case 'paint':
            case 'measure':
                _addMetricSpan(span, entry as PerformanceResourceTiming, navigateEntry, timeOrigin);
                break;

            default:
                break;
        }
    });
}

function _addMetricSpan(
    span: Sentry.Span,
    entry: PerformanceResourceTiming,
    navigateEntry: PerformanceEntry | undefined,
    timeOrigin: number
) {
    const requestTime = navigateEntry ? navigateEntry.startTime / 1000 : 0;
    const startTime = entry.startTime / 1000;
    const duration = Math.max(0, entry.duration) / 1000;
    const measureStartTimestamp = timeOrigin + Math.max(startTime, requestTime);
    const startTimeStamp = timeOrigin + startTime;
    const measureEndTimestamp = startTimeStamp + duration;

    const attributes: SpanAttributes = {
        [Sentry.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.resource.browser.metrics',
    };

    if (measureStartTimestamp !== startTimeStamp) {
        attributes['sentry.browser.measure_happened_before_request'] = true;
        attributes['sentry.browser.measure_start_time'] = measureStartTimestamp;
    }

    if (measureStartTimestamp <= measureEndTimestamp) {
        Sentry.withActiveSpan(span, () => {
            const span = Sentry.startInactiveSpan({
                startTime: measureStartTimestamp,
                name: entry.name.replace(window.location.origin, ''),
                op: entry.initiatorType ? `resource.${entry.initiatorType}` : 'resource.other',
                attributes,
            });

            if (span) {
                span.end(measureEndTimestamp);
            }

            return span;
        });
    }
}

function _addResourceSpan(span: Sentry.Span, entry: PerformanceResourceTiming, timeOrigin: number) {
    if (entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch') {
        return;
    }
    const startTime = entry.startTime / 1000;
    const duration = Math.max(0, entry.duration) / 1000;
    const parsedUrl = parseUrl(entry.name);

    const attributes: SpanAttributes = {
        [Sentry.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.resource.browser.metrics',
    };

    // Types do not reflect this property yet
    const renderBlockingStatus = (
        entry as { renderBlockingStatus?: 'render-blocking' | 'non-render-blocking' }
    ).renderBlockingStatus;
    if (renderBlockingStatus) {
        attributes['resource.render_blocking_status'] = renderBlockingStatus;
    }
    if (parsedUrl.host) {
        attributes['server.address'] = parsedUrl.host;
    }
    if (parsedUrl.protocol) {
        attributes['url.scheme'] = parsedUrl.protocol.split(':').pop(); // the protocol returned by parseUrl includes a :, but OTEL spec does not, so we remove it.
    }

    const startTimestamp = timeOrigin + startTime;
    const endTimestamp = startTimestamp + duration;

    Sentry.withActiveSpan(span, () => {
        // const parentStartTime = Sentry.spanToJSON(span).start_timestamp;
        // if (parentStartTime && parentStartTime > startTimestamp) {
        //     // We can only do this for SentrySpans...
        //     if (typeof (span as Partial<SentrySpan>).updateStartTime === 'function') {
        //         (parentSpan as SentrySpan).updateStartTime(startTimeInSeconds);
        //     }
        // }

        const span = Sentry.startInactiveSpan({
            startTime: startTimestamp,
            name: entry.name.replace(window.location.origin, ''),
            op: entry.initiatorType ? `resource.${entry.initiatorType}` : 'resource.other',
            attributes,
        });

        if (span) {
            span.end(endTimestamp);
        }

        return span;
    });
}

/**
 *  This integration patches the browser's built-in APIs to track navigation and long tasks.
 *
 * @returns
 */
export const browserTracingIntegration = () => {
    registerSpanErrorInstrumentation();
    startTrackingLongTasks();
    startTrackingINP();
    const _collectWebVitals = startTrackingWebVitals({ recordClsStandaloneSpans: false });

    function _createRouteSpan(client: Client, startSpanOptions: StartSpanOptions) {
        const previousSpan = Sentry.getActiveSpan();
        const span = Sentry.startInactiveSpan(startSpanOptions);
        _setSpanForScope(getCurrentScope(), span);

        span.end = new Proxy(span.end, {
            apply(target, thisArg, args: Parameters<Sentry.Span['end']>) {
                _collectWebVitals();
                addPerformanceEntries(span, { recordClsOnPageloadSpan: false });
                setActiveIdleSpan(client, undefined);
                const scope = Sentry.getCurrentScope();
                const oldPropagationContext = scope.getPropagationContext();
                scope.setPropagationContext({
                    ...oldPropagationContext,
                    traceId: span.spanContext().traceId,
                    sampled: spanIsSampled(span),
                    dsc: getDynamicSamplingContextFromSpan(span),
                });

                const [definedEndTimestamp] = args;
                const childSpans = Sentry.getSpanDescendants(span).filter(
                    (child) => child !== span
                );
                _setSpanForScope(scope, previousSpan);

                const spanJSON = Sentry.spanToJSON(span);

                const { start_timestamp: startTimestamp } = spanJSON;
                // This should never happen, but to make TS happy...
                if (!startTimestamp) {
                    return;
                }

                childSpans.forEach((childSpan) => {
                    if (childSpan.isRecording()) {
                        childSpan.setStatus({ code: SPAN_STATUS_ERROR, message: 'cancelled' });
                        childSpan.end(definedEndTimestamp);
                    }
                });

                return Reflect.apply(target, thisArg, args);
            },
        });

        setActiveIdleSpan(client, span);

        const WINDOW =
            typeof window !== 'undefined' && 'addEventListener' in window ? window : undefined;

        // End idle span when the user F5 refreshes the page or closes the tab
        WINDOW?.addEventListener('beforeunload', () => {
            client.emit('idleSpanEnableAutoFinish', span);
            span.end();
        });
    }

    return {
        name: 'BrowserTracing',
        afterAllSetup: (client) => {
            function maybeEndActiveSpan(): void {
                const activeSpan = get(client, ACTIVE_IDLE_SPAN_PROPERTY) as
                    | Sentry.Span
                    | undefined;
                if (activeSpan && !Sentry.spanToJSON(activeSpan).timestamp) {
                    activeSpan.end();
                }
            }

            client.on('startNavigationSpan', (startSpanOptions) => {
                if (Sentry.getClient() !== client) {
                    return;
                }

                maybeEndActiveSpan();

                getIsolationScope().setPropagationContext({
                    traceId: generateTraceId(),
                });
                getCurrentScope().setPropagationContext({
                    traceId: generateTraceId(),
                });

                _createRouteSpan(client, {
                    op: 'navigation',
                    ...startSpanOptions,
                });
            });

            registerInpInteractionListener();
        },
    } as Integration;
};
